import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./AdminSidebar.scss";

const PURCHASE_URL = "https://ourdept.com";

const AdminSidebar = ({ isOpen, onClose }) => {
  const { canAccessModule, hasAnyAuthority, hasRole } = useAuth();
  const location = useLocation();

  const hasHrmAccess =
    hasRole("ROLE_ADMIN") &&
    ["HRM", "LEAVE", "ATTENDANCE", "PAYROLL", "EMAIL_AUTOMATION"].some(
      canAccessModule,
    );

  const hasCrmAccess =
    hasAnyAuthority([
      "ROLE_CRM-ADMIN",
      "ROLE_ERP-ADMIN",
      "ROLE_INVENTORY-MANAGER",
    ]) && canAccessModule("CRM");

  const items = [
    {
      name: "Company",
      icon: "mdi:domain",
      to: "/company-details/info",
      access: hasRole("ROLE_ADMIN") && canAccessModule("ADMIN"),
      routes: ["/company-details"],
    },
    {
      name: "HRM",
      icon: "mdi:account-group-outline",
      to: "/admin/leave-types",
      access: hasHrmAccess,
      routes: [
        "/admin/leave-types",
        "/admin/attendance-location",
        "/admin/salary-master",
        "/admin/payment-setup",
        "/admin/reimbursements",
        "/admin/allowances",
        "/admin/deductions",
        "/admin/income-tax",
        "/admin/professional-tax",
      ],
    },
    {
      name: "CRM",
      icon: "mdi:handshake-outline",
      to: "/crm/master",
      access: hasCrmAccess,
      routes: ["/crm"],
    },
    {
      name: "Notifications",
      icon: "mdi:bell-outline",
      to: "/admin/notification",
      access: true,
      routes: ["/admin/notification"],
    },
    {
      name: "Automation",
      icon: "mdi:robot-outline",
      to: "/admin/automation",
      access: true,
      routes: ["/admin/automation"],
    },
  ];

  useEffect(() => {
    document.documentElement.style.setProperty("--admin-sidebar-width", "64px");
  }, []);

  // Close on Escape (only matters while open on mobile)
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const sidebar = (
    <>
      {/* Backdrop: rendered right before the sidebar, same parent (body) */}
      <div
        className={`admin-sidebar-backdrop ${
          isOpen ? "admin-sidebar-backdrop--visible" : ""
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        id="admin-navigation"
        className={`admin-sidebar ${isOpen ? "admin-sidebar--open" : ""}`}
        aria-label="Admin navigation"
      >
        <nav className="admin-sidebar__menu">
          {items.map((item) => {
            const active = item.routes.some(
              (route) =>
                location.pathname === route ||
                (route !== "/admin" && location.pathname.startsWith(route)),
            );

            const content = (
              <>
                <Icon className="admin-sidebar__icon" icon={item.icon} />
                <span className="admin-sidebar__name">{item.name}</span>
                {!item.access && (
                  <Icon
                    className="admin-sidebar__lock"
                    icon="mdi:lock-outline"
                  />
                )}
              </>
            );

            return item.access ? (
              <Link
                key={item.name}
                to={item.to}
                className={`admin-sidebar__item ${
                  active ? "admin-sidebar__item--active" : ""
                }`}
                aria-label={item.name}
                title={item.name}
                onClick={onClose}
              >
                {content}
              </Link>
            ) : (
              <button
                key={item.name}
                type="button"
                className="admin-sidebar__item admin-sidebar__item--locked"
                onClick={() => window.location.assign(PURCHASE_URL)}
                aria-label={`${item.name}: module not purchased`}
                title={`${item.name}: module not purchased. Click to view plans.`}
              >
                {content}
              </button>
            );
          })}
        </nav>
      </aside>
    </>
  );

  return createPortal(sidebar, document.body);
};

export default AdminSidebar;
